import { PropsWithStyles, StyledProps } from '@front10';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import React, { PropsWithChildren, useContext } from 'react';
import { useTranslation } from '@front10/utils';
import FareItemRow from './FareItemRow';

export type FareTableField = 'origin' | 'destination' | 'dates' | 'fare-type' | 'price' | 'book';

interface Props {
  columns?: FareTableField[];
  data: FareItem[];
  focusOn: string;
  selectedItemRef: React.Ref<HTMLTableRowElement>;
  itemSettings: $TSFixMe;
  headSettings?: HeadProps;
  bodySettings?: BodyProps;
  headingItemSettings?: StyledProps;
}

export function FareTable({
  columns = ['origin', 'destination', 'dates', 'fare-type', 'price'],
  data,
  focusOn,
  itemSettings,
  selectedItemRef,
  headingItemSettings = {},
  headSettings,
  bodySettings,
  ...props
}: Props & TableProps) {
  const translate = useTranslation();
  const { theme, direction } = useContext(Context);
  const [headerClass, headerStyles] = useStyles(headingItemSettings, { direction, theme });

  return (
    <Table {...props}>
      <TableHead {...headSettings}>
        <tr>
          {columns.map((header) => (
            <th scope="col" key={header} className={headerClass} style={headerStyles}>
              {translate(`cmp-fare-table-heading-${header}`, header)}
            </th>
          ))}
        </tr>
      </TableHead>
      <TableBody {...bodySettings}>
        {data.map((item) => (
          <FareItemRow
            ref={item.index === focusOn ? selectedItemRef : null}
            key={item.id}
            fareItem={item}
            fields={columns}
            {...itemSettings}
          />
        ))}
      </TableBody>
    </Table>
  );
}

type TableProps = PropsWithChildren<PropsWithStyles<JSX.IntrinsicElements['table']>>;

function Table({ children, styles = {}, ...props }: TableProps) {
  const { theme, direction, translate } = useContext(Context);
  const [className, style] = useStyles(styles, { theme, direction });
  return (
    <table
      className={`fr-table ${className}`}
      aria-label={translate('cmp-fare-table-caption', 'Best fares on flights')}
      style={style}
      {...props}
    >
      {children}
    </table>
  );
}

type HeadProps = PropsWithChildren<PropsWithStyles<JSX.IntrinsicElements['thead']>>;

function TableHead({ children, styles = {}, ...props }: HeadProps) {
  const { theme, direction } = useContext(Context);
  const [className, style] = useStyles(styles, { theme, direction });
  return (
    <thead className={`fr-thead-light ${className}`} style={style} {...props}>
      {children}
    </thead>
  );
}

type BodyProps = PropsWithChildren<PropsWithStyles<JSX.IntrinsicElements['tbody']>>;

function TableBody({ children, styles = {}, ...props }: BodyProps) {
  const { theme, direction } = useContext(Context);
  const [className, style] = useStyles(styles, { theme, direction });
  return (
    <tbody className={`fr-thead-light ${className}`} style={style} {...props}>
      {children}
    </tbody>
  );
}
